
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { FieldError } from "../FieldError";

interface PropertyBasicDetailsProps {
  bedrooms: string;
  setBedrooms: (value: string) => void;
  bathrooms: string;
  setBathrooms: (value: string) => void;
  squareFeet: string;
  setSquareFeet: (value: string) => void;
  propertyType: string;
  setPropertyType: (value: string) => void;
  units: string;
  setUnits: (value: string) => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

export const PropertyBasicDetailsSection = ({
  bedrooms,
  setBedrooms,
  bathrooms,
  setBathrooms,
  squareFeet,
  setSquareFeet,
  propertyType,
  setPropertyType,
  units,
  setUnits,
  errors = {},
  clearFieldError,
}: PropertyBasicDetailsProps) => {
  const errCls = (field: string) =>
    errors[field] ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <div className="space-y-2">
        <Label htmlFor="bedrooms">Bedrooms*</Label>
        <Input
          id="bedrooms"
          type="number"
          min="1"
          step="1"
          placeholder="e.g. 3"
          required
          aria-invalid={!!errors.bedrooms}
          className={cn("bg-muted", errCls("bedrooms"))}
          value={bedrooms}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || parseFloat(value) >= 0) {
              setBedrooms(value);
              clearFieldError?.("bedrooms");
            }
          }}
        />
        <FieldError message={errors.bedrooms} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="bathrooms">Bathrooms*</Label>
        <Input
          id="bathrooms"
          type="number"
          min="1"
          step="0.5"
          placeholder="e.g. 2"
          required
          aria-invalid={!!errors.bathrooms}
          className={cn("bg-muted", errCls("bathrooms"))}
          value={bathrooms}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || parseFloat(value) >= 0) {
              setBathrooms(value);
              clearFieldError?.("bathrooms");
            }
          }}
        />
        <FieldError message={errors.bathrooms} />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="squareFeet">Square Footage*</Label>
        <Input 
          id="squareFeet" 
          type="number" 
          placeholder="1800" 
          required 
          min="0"
          aria-invalid={!!errors.squareFeet}
          className={cn("bg-muted", errCls("squareFeet"))}
          value={squareFeet}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || parseFloat(value) >= 0) {
              setSquareFeet(value);
              clearFieldError?.("squareFeet");
            }
          }} 
        />
        <FieldError message={errors.squareFeet} />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="propertyType">Property Type*</Label>
        <Select
          value={propertyType}
          onValueChange={(v) => {
            setPropertyType(v);
            clearFieldError?.("propertyType");
            // If switching away from multi-unit, stale units error is no longer meaningful
            if (v !== 'multi-unit') clearFieldError?.("units");
          }}
          required
        >
          <SelectTrigger id="propertyType" aria-invalid={!!errors.propertyType} className={cn("bg-muted", errCls("propertyType"))}>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single-family">Single Family</SelectItem>
            <SelectItem value="multi-family">Multi Family</SelectItem>
            <SelectItem value="multi-unit">Multi Unit</SelectItem>
            <SelectItem value="condo">Condo</SelectItem>
            <SelectItem value="townhouse">Townhouse</SelectItem>
            <SelectItem value="land">Land</SelectItem>
            <SelectItem value="commercial">Commercial</SelectItem>
          </SelectContent>
        </Select>
        <FieldError message={errors.propertyType} />
      </div>

      {propertyType === 'multi-unit' && (
        <div className="space-y-2">
          <Label htmlFor="units">Number of Units*</Label>
          <Input
            id="units"
            name="units"
            type="number"
            min="2"
            step="1"
            placeholder="e.g. 4"
            required
            aria-invalid={!!errors.units}
            className={cn("bg-muted", errCls("units"))}
            value={units}
            onChange={(e) => { setUnits(e.target.value); clearFieldError?.("units"); }}
          />
          <FieldError message={errors.units} />
        </div>
      )}
    </div>

  );
};
